import { CallApi, handleJSONResponse, handleResponse } from './common';
import { RecordLabel } from '@common/types/record-label/record-label';
import { NewRecordLabelRelation } from '@services/record-labels/types/new-record-label-relation';
import { RecordLabelRelation } from '@services/record-labels/types/record-label-relation';
import { Operator } from '@services/record-labels/types/operator';
import InviteExistingArtistRequest from '@common/types/record-label/invite-existing-artist-request';
import InviteOperatorRequest from '@common/types/record-label/invite-operator-request';
import { DowngradeRecordLabelRequest } from '@common/types/record-label/downgrade-record-label-request';

const callAPI = CallApi('record-labels');

export const create = async (
  name?: string,
  merchOwnership?: number,
  recordingOwnership?: number
): Promise<RecordLabel> => {
  const response = await callAPI('/', 'POST', {
    name,
    merchOwnership,
    recordingOwnership,
  });

  return await handleJSONResponse(response);
};

export const list = async (): Promise<RecordLabel[]> => {
  const response = await callAPI('/', 'GET');

  return await handleJSONResponse(response);
};

export const validateName = async (labelName: string): Promise<boolean> => {
  const response = await callAPI('/', 'GET');
  const labels = await handleJSONResponse(response);

  if (labels.length > 0) {
    const nameMatch = labels.every(
      (item: RecordLabel) => item.name === labelName
    );
    return !nameMatch;
  }
  return true;
};

export const memberships = async (
  artistId: string
): Promise<RecordLabelRelation[]> => {
  const response = await callAPI(`/membership/${artistId}`, 'GET');

  return await handleJSONResponse(response);
};

export const listArtists = async (
  recordLabelId: string
): Promise<RecordLabelRelation[]> => {
  const response = await callAPI(`/${recordLabelId}`, 'GET');

  return await handleJSONResponse(response);
};

export const downgrade = async (
  recordLabelId: string,
  payload: DowngradeRecordLabelRequest
): Promise<RecordLabel> => {
  const response = await callAPI(
    `/${recordLabelId}/downgrade`,
    'POST',
    payload
  );

  return await handleJSONResponse(response);
};

export const update = async (
  recordLabel: RecordLabel
): Promise<RecordLabel> => {
  const response = await callAPI(`/${recordLabel.id}`, 'PUT', recordLabel);

  return await handleJSONResponse(response);
};

export const updateRelation = async (
  recordLabelRelation: RecordLabelRelation
): Promise<RecordLabelRelation> => {
  const response = await callAPI(
    `/${recordLabelRelation.recordLabelId}/${recordLabelRelation.artistId}`,
    'PUT',
    recordLabelRelation
  );

  return await handleJSONResponse(response);
};

export const remove = async (recordLabelId: string): Promise<void> => {
  const response = await callAPI(`/${recordLabelId}`, 'DELETE');

  await handleResponse(response);
};

export const attachRelation = async (
  relation: NewRecordLabelRelation
): Promise<RecordLabelRelation> => {
  const response = await callAPI(
    `/${relation.recordLabelId}/${relation.artistId}`,
    'POST',
    relation
  );

  return await handleJSONResponse(response);
};

export const removeRelation = async (
  relation: NewRecordLabelRelation
): Promise<void> => {
  const response = await callAPI(
    `/${relation.recordLabelId}/${relation.artistId}`,
    'DELETE'
  );

  await handleResponse(response);
};

export const getRecordLabelSplit = async (
  recordLabelId: string,
  artistId: string
): Promise<number> => {
  const response = await callAPI(`/${recordLabelId}/${artistId}`, 'GET');

  return Number(await response.text());
};

export const listOperators = async (
  recordLabelId: string
): Promise<Operator[]> => {
  const response = await callAPI(`/${recordLabelId}/operators`, 'GET');

  return await handleJSONResponse(response);
};

export const createOperator = async (
  recordLabelId: string,
  userName: string,
  userId: string
): Promise<Operator> => {
  const response = await callAPI(`/${recordLabelId}/operators`, 'POST', {
    userName: userName,
    userId: userId,
  });

  return await handleJSONResponse(response);
};

export const removeOperator = async (
  recordLabelId: string,
  operatorId: string
): Promise<void> => {
  const response = await callAPI(
    `/${recordLabelId}/operators/${operatorId}`,
    'DELETE'
  );

  await handleResponse(response);
};

export const inviteRecordLabel = async (
  recordLabelId: string,
  invite: InviteExistingArtistRequest
): Promise<Operator> => {
  const response = await callAPI(
    `/${recordLabelId}/invites/artist`,
    'POST',
    invite
  );

  return await handleJSONResponse(response);
};

export const inviteOperator = async (
  recordLabelId: string,
  invite: InviteOperatorRequest
): Promise<Operator> => {
  const response = await callAPI(
    `/${recordLabelId}/invites/operator`,
    'POST',
    invite
  );

  return await handleJSONResponse(response);
};

export const restoreRecordLabel = async (
  recordLabelId: string,
  artists: string[]
): Promise<Operator> => {
  const response = await callAPI(`/${recordLabelId}/restore`, 'POST', artists);

  return await handleJSONResponse(response);
};
